/**
 * Vendor Repository Implementation
 * Implements the VendorRepository interface
 * Coordinates between API calls and mappers
 */

import { VendorRepository } from "../../domain/repositories/VendorRepository";
import {
  VendorEntity,
  VendorOperationResult,
  VendorDetailResult,
  VendorSearchResultEntity,
} from "../../domain/entities/VendorEntity";
import {
  createVendor,
  updateVendor,
  getVendorById,
  searchVendors,
} from "../api/vendorsApi";
import { VendorMapper } from "../mappers/VendorMapper";
import { VendorDataDTO } from "../models/VendorDTOs";

export class VendorRepositoryImpl implements VendorRepository {
  /**
   * Create a new vendor
   */
  async createVendor(
    vendorData: Partial<VendorEntity>
  ): Promise<VendorOperationResult> {
    const vendorDataDTO = VendorMapper.toVendorDataDTO(
      vendorData as VendorEntity
    );

    const response = await createVendor(vendorDataDTO);
    const responseData = response.data as {
      vendor_id?: number;
      vendorid?: string;
      zoho_vendorid?: string;
    };

    if (responseData?.vendor_id != null) {
      const entity = VendorMapper.toVendorEntity({
        ...vendorDataDTO,
        vendorid: String(responseData.vendor_id),
        zoho_vendorid:
          responseData.zoho_vendorid ?? vendorData.zoho_vendorid ?? "",
      } as VendorDataDTO);

      return {
        success: response.success,
        message: response.message,
        data: entity,
      };
    }

    return VendorMapper.toVendorOperationResult(response);
  }

  /**
   * Update an existing vendor
   */
  async updateVendor(
    vendorId: string,
    vendorData: Partial<VendorEntity>
  ): Promise<VendorOperationResult> {
    const vendorDataDTO = vendorData as any;

    const response = await updateVendor(vendorId, vendorDataDTO);
    const responseData = response.data as { vendor_id?: number; vendorid?: string };

    if (responseData?.vendor_id != null) {
      const entity = VendorMapper.toVendorEntity({
        ...vendorDataDTO,
        vendorid: String(responseData.vendor_id),
        zoho_vendorid: vendorData.zoho_vendorid ?? "",
      } as VendorDataDTO);

      return {
        success: response.success,
        message: response.message,
        data: entity,
      };
    }

    return VendorMapper.toVendorOperationResult(response);
  }

  /**
   * Get vendor by ID
   */
  async getVendorById(vendorId: string): Promise<VendorDetailResult> {
    // Call API
    const response = await getVendorById(vendorId);

    // Map to domain entity
    return VendorMapper.toVendorDetailResult(response);
  }

  /**
   * Search vendors with pagination
   */
  async searchVendors(params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<VendorSearchResultEntity> {
    // Call API
    const response = await searchVendors(params);

    // Map to domain entities
    return VendorMapper.toVendorSearchResult(response);
  }
}

